// Avoid circular dependency by importing from common-utils/src/Money
import { round2dp } from '@tix/common-utils/src/Money'
import type { Schema } from '@tix/openapi-sdk'

export type CartFeeLine = {
  ticketGroupId: string
  eventTemplateId: string
  ticketTypeId: string
  ticketIds: string[]
  label: string
  amount: number
}

export type CartFeeLinesResult = {
  data: CartFeeLine[] | null
  error: Error | null
}

/**
 * TODO: Replace the three arguments with a single Cart entity containing the three entities
 * when web-client is migrated to Vue 3.
 */
export function cartFeeLines(
  tickets: Schema<'ticket'>[],
  ticketGroups: Schema<'ticket_group'>[],
  ticketTypes: Schema<'ticket_type'>[],
): CartFeeLinesResult {
  const groupsById = new Map(ticketGroups.map((group) => [group.id, group]))
  const typesById = new Map(ticketTypes.map((type) => [type.id, type]))
  const collected = new Map<string, CartFeeLine>()

  for (const ticket of tickets) {
    const group = groupsById.get(ticket.ticket_group_id)
    if (!group) {
      return {
        data: null,
        error: new Error(`Fee ticket ${ticket.id} is missing its ticket group ${ticket.ticket_group_id}`),
      }
    }
    if (group.handler !== 'fee') continue

    const type = typesById.get(ticket.ticket_type_id)
    if (!type) {
      return {
        data: null,
        error: new Error(
          `Fee ticket ${ticket.id} in group ${ticket.ticket_group_id} references missing ticket type ${ticket.ticket_type_id}`,
        ),
      }
    }

    const existing = collected.get(type.id)
    const parsed = Number(ticket.face_value)
    const amount = Number.isFinite(parsed) ? parsed : 0

    if (existing) {
      existing.amount = round2dp(existing.amount + amount)
      existing.ticketIds.push(ticket.id)
    } else {
      collected.set(type.id, {
        ticketTypeId: type.id,
        ticketGroupId: ticket.ticket_group_id,
        eventTemplateId: ticket.event_template_id,
        label: type.name,
        ticketIds: [ticket.id],
        amount,
      })
    }
  }

  const data = [...collected.values()].sort((a, b) => {
    const aLabel = a.label.toLowerCase()
    const bLabel = b.label.toLowerCase()
    if (aLabel !== bLabel) return aLabel < bLabel ? -1 : 1
    return 0
  })

  return { data, error: null }
}
